import React, { useRef, useEffect, useState } from 'react';
import { useWorldStore } from '../stores/worldStore';
import { startScreenShare, stopScreenShare, isPresenting } from '../services/screenShareService';
import './ScreenShare.css';
import { normalizePanelUrl } from '../utils/panelUrl';

interface ScreenShareProps { onClose: () => void; onPlace?: () => void; onVideoPlace?: (url: string) => void; }
export default function ScreenShare({ onClose, onPlace, onVideoPlace }: ScreenShareProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const stream = useWorldStore(s => s.activeScreenShare);
  const canCapture = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;
  const [videoUrl, setVideoUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.srcObject = stream;
    if (stream) void video.play().catch(() => setError('Preview could not play. Try reopening this panel.'));
    return () => { video.srcObject = null; };
  }, [stream]);
  const start = async () => {
    setBusy(true); setError('');
    try { await startScreenShare(); }
    catch (err) { setError(err instanceof Error ? err.message : 'Unable to start screen sharing.'); }
    finally { setBusy(false); }
  };
  return <div className="screen-share-overlay">
    <div className="screen-share-container glassmorphism-dark" role="dialog" aria-modal="true" aria-label="Share screen">
      <div className="screen-share-header"><h3>Share Screen</h3><button className="close-btn" aria-label="Close screen sharing controls" onClick={onClose}>×</button></div>
      <p>Join voice to connect with the room, then choose a tab, window, or screen. Your share appears on the placed screens.</p>
      <div className="screen-share-content">
        {stream ? <video ref={videoRef} autoPlay muted playsInline className="screen-share-video" /> : <div className="screen-share-placeholder"><p>Your shared screen will appear here.</p></div>}
      </div>
      {error && <p role="alert">{error}</p>}
      <div className="screen-share-controls">
        {!canCapture && <p>Start sharing from a desktop browser connected to this world. This device can watch the shared screen.</p>}
        {!isPresenting() && canCapture && <button className="btn-primary" disabled={busy} onClick={start}>{busy ? 'Starting…' : 'Choose Screen'}</button>}
        {onPlace && <button className="btn-primary" onClick={onPlace}>Place Screen</button>}
        {isPresenting() && <button className="btn-glass" disabled={busy} onClick={async () => { setBusy(true); await stopScreenShare(); setBusy(false); }}>Stop Sharing</button>}
        <button className="btn-glass" onClick={onClose}>Back to World</button>
      </div>
      {onVideoPlace && <form className="panel-picker" onSubmit={e => { e.preventDefault(); try { onVideoPlace(normalizePanelUrl(videoUrl)); } catch { setError('Enter a valid video URL.'); } }}>
        <label htmlFor="screen-video-url">Or place a video file</label>
        <input id="screen-video-url" placeholder="https://example.com/video.mp4" value={videoUrl} onChange={e => setVideoUrl(e.target.value)} required />
        <button className="btn-glass" type="submit">Place Video</button>
      </form>}
      {stream && <p>Sharing continues when you close this panel. Use Stop Sharing to end it.</p>}
    </div>
  </div>;
}
